/**
 * popupStack.js — Shared layer registry for Popup and ClickableDropdown.
 *
 * Module-level (not React context) so it works across the full component tree
 * regardless of render hierarchy. Both Popup and ClickableDropdown register here
 * so their z-indices are always consistent with each other.
 *
 * Z-index layout (starts above DS Tooltip at 1100):
 *   Stack index 0  →  1200
 *   Stack index 1  →  1250   ← above first popup's panel (1201)
 *   Stack index 2  →  1300
 *   ...each step of 50 leaves room for overlay (n) vs panel (n+1)
 */

const BASE_Z_INDEX = 1200;
const STACK_STEP = 50;

const stack = [];
const scrollLockOwners = new Set();
const listeners = new Set();
let idCounter = 0;

// ── ID generation ────────────────────────────────────────────────────────────

export const createLayerId = () => `layer-${idCounter++}`;

// ── Stack listeners (allow components to re-render when stack changes) ───────

const notifyListeners = () => listeners.forEach((fn) => fn((v) => v + 1));

export const subscribeToStack = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// ── Stack operations ─────────────────────────────────────────────────────────

export const pushLayer = (id) => {
    if (!stack.includes(id)) {
        stack.push(id);
        notifyListeners();
    }
};

export const popLayer = (id) => {
    const index = stack.indexOf(id);
    if (index !== -1) {
        stack.splice(index, 1);
        notifyListeners();
    }
};

export const isTopLayer = (id) => stack[stack.length - 1] === id;

export const getLayerZIndex = (id) => {
    const index = stack.indexOf(id);
    return BASE_Z_INDEX + (index === -1 ? stack.length : index) * STACK_STEP;
};

// ── Scroll lock with reference counting ──────────────────────────────────────
// Multiple open popups each call addScrollLock; body is only unlocked when
// ALL of them close (last owner removed).

export const addScrollLock = (id) => {
    if (typeof document === 'undefined') return;
    scrollLockOwners.add(id);
    if (scrollLockOwners.size === 1) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.dataset.prevOverflow = document.body.style.overflow || '';
        document.body.dataset.prevPaddingRight = document.body.style.paddingRight || '';
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
};

export const removeScrollLock = (id) => {
    if (typeof document === 'undefined') return;
    scrollLockOwners.delete(id);
    if (scrollLockOwners.size === 0) {
        document.body.style.overflow = document.body.dataset.prevOverflow || '';
        document.body.style.paddingRight = document.body.dataset.prevPaddingRight || '';
        delete document.body.dataset.prevOverflow;
        delete document.body.dataset.prevPaddingRight;
    }
};
